'use strict';

MxFilterOperatorsFactory.$inject = ['mx.internationalization'];

export function MxFilterOperatorsFactory(i18n) {
	const operators = {
		Equals: {
			value: 1,
			name: i18n.get('solutionBuilderAgent.components.mx-search-query-condition-operator.equals'),
			label: '=',
			fn: (target, values) => target === values[0],
		},
		NotEquals: {
			value: 2,
			name: i18n.get('solutionBuilderAgent.components.mx-search-query-condition-operator.notequals'),
			label: '≠',
			fn: (target, values) => target !== values[0],
		},
		LowerThan: {
			value: 3,
			name: i18n.get('solutionBuilderAgent.components.mx-search-query-condition-operator.lowerthan'),
			label: '<',
			fn: (target, values) => target < values[0],
		},
		LowerThanOrEquals: {
			value: 4,
			name: i18n.get('solutionBuilderAgent.components.mx-search-query-condition-operator.lowerthanorequals'),
			label: '≤',
			fn: (target, values) => target <= values[0],
		},
		GreaterThan: {
			value: 5,
			name: i18n.get('solutionBuilderAgent.components.mx-search-query-condition-operator.greaterthan'),
			label: '>',
			fn: (target, values) => target > values[0],
		},
		GreaterThanOrEquals: {
			value: 6,
			name: i18n.get('solutionBuilderAgent.components.mx-search-query-condition-operator.greaterthanorequals'),
			label: '≥',
			fn: (target, values) => target >= values[0],
		},
		Contains: {
			value: 7,
			name: i18n.get('solutionBuilderAgent.components.mx-search-query-condition-operator.contains'),
			fn: (target, values) => typeof target === 'string' && target.indexOf(values[0]) !== -1,
		},
		StartsWith: {
			value: 8,
			name: i18n.get('solutionBuilderAgent.components.mx-search-query-condition-operator.startswith'),
			fn: (target, values) => typeof target === 'string' && target.indexOf(values[0]) === 0,
		},
		EndsWith: {
			value: 9,
			name: i18n.get('solutionBuilderAgent.components.mx-search-query-condition-operator.endswith'),
			fn: (target, values) => {
				const value = values[0] || '';
				return typeof target === 'string' && target.indexOf(value) === target.length - value.length;
			},
		},
		In: {
			value: 10,
			name: i18n.get('solutionBuilderAgent.components.mx-search-query-condition-operator.in'),
			isArray: true,
			fn: (target, values) => values.some((value) => target === value),
		},
		NotIn: {
			value: 11,
			name: i18n.get('solutionBuilderAgent.components.mx-search-query-condition-operator.notin'),
			isArray: true,
			fn: (target, values) => !values.some((value) => target === value),
		},
		Between: {
			value: 12,
			name: i18n.get('solutionBuilderAgent.components.mx-search-query-condition-operator.between'),
			fn: (target, values) => values[0] < target && target < values[1],
		},
		FlexDateBetween: {
			value: 13,
			name: i18n.get('solutionBuilderAgent.components.mx-search-query-condition-operator.flexdatebetween'),
			fn: (target, values) => values[0] < target && target < values[1],
		},
		IsNotSet: {
			value: 14,
			name: i18n.get('solutionBuilderAgent.components.mx-search-query-condition-operator.isnotset'),
			withoutParams: true,
			fn: (target) => target === null || typeof target !== 'undefined',
		},
		IsSet: {
			value: 15,
			name: i18n.get('solutionBuilderAgent.components.mx-search-query-condition-operator.isset'),
			withoutParams: true,
			fn: (target) => target !== null && typeof target !== 'undefined',
		},
		NotContains: {
			value: 16,
			name: i18n.get('solutionBuilderAgent.components.mx-search-query-condition-operator.notcontains'),
			fn: (target, values) => typeof target !== 'string' || target.indexOf(values[0]) === -1,
		},
		ContainsAnyWord: {
			value: 21,
			name: i18n.get('solutionBuilderAgent.components.mx-search-query-condition-operator.containsAnyWord'),
			label: i18n.get('solutionBuilderAgent.components.mx-search-query-condition-operator.containsAnyWordLabel'),
			fn: (target, values) => getWords(values[0] || '').some((w) => target.includes(w)),
		},
		ContainsAllWords: {
			value: 22,
			name: i18n.get('solutionBuilderAgent.components.mx-search-query-condition-operator.containsAllWords'),
			label: i18n.get('solutionBuilderAgent.components.mx-search-query-condition-operator.containsAllWordsLabel'),
			fn: (target, values) => getWords(values[0] || '').every((w) => target.includes(w)),
		},
		DoesNotContainAnyWord: {
			value: 23,
			name: i18n.get('solutionBuilderAgent.components.mx-search-query-condition-operator.doesNotContainAnyWord'),
			label: i18n.get('solutionBuilderAgent.components.mx-search-query-condition-operator.doesNotContainAnyWordLabel'),
			fn: (target, values) => !getWords(values[0] || '').some((w) => target.includes(w)),
		},
		DoesNotContainAllWords: {
			value: 24,
			name: i18n.get('solutionBuilderAgent.components.mx-search-query-condition-operator.doesNotContainAllWords'),
			label: i18n.get('solutionBuilderAgent.components.mx-search-query-condition-operator.doesNotContainAllWordsLabel'),
			fn: (target, values) => !getWords(values[0] || '').every((w) => target.includes(w)),
		},
	};

	Object.keys(operators).forEach((o) => {
		operators[o].title = operators[o].name; // to support legacy with op.title
		operators[o].technicalName = o;
	});
	Object.freeze(operators);

	const getOperatorsByType = (type) => {
		switch (type) {
			case 'Pickup':
			case 'ObjectReference':
			case 'Relation':
				return [
					operators.In,
					operators.NotIn,
					operators.Contains,
					operators.NotContains,
					operators.StartsWith,
					operators.EndsWith,
					operators.IsSet,
					operators.IsNotSet,
					operators.ContainsAllWords,
					operators.ContainsAnyWord,
					operators.DoesNotContainAnyWord,
				];
			case 'String':
				return [
					operators.Contains,
					operators.NotContains,
					operators.Equals,
					operators.NotEquals,
					operators.StartsWith,
					operators.EndsWith,
					operators.IsSet,
					operators.IsNotSet,
					operators.ContainsAllWords,
					operators.ContainsAnyWord,
					operators.DoesNotContainAnyWord,
				];

			case 'Boolean':
				return [operators.Equals, operators.NotEquals];

			case 'Guid':
				return [operators.Equals, operators.NotEquals, operators.IsSet, operators.IsNotSet];

			case 'Number':
			case 'Integer':
			case 'Long':
			case 'Float':
			case 'Decimal':
			case 'Currency':
				return [
					operators.Equals,
					operators.NotEquals,
					operators.LowerThan,
					operators.LowerThanOrEquals,
					operators.GreaterThan,
					operators.GreaterThanOrEquals,
					operators.Between,
					operators.IsSet,
					operators.IsNotSet,
				];

			case 'Date':
				return [operators.Between, operators.FlexDateBetween, operators.IsSet, operators.IsNotSet];

			case 'Text':
				return [
					operators.Contains,
					operators.NotContains,
					operators.StartsWith,
					operators.EndsWith,
					operators.IsSet,
					operators.IsNotSet,
					operators.ContainsAllWords,
					operators.ContainsAnyWord,
					operators.DoesNotContainAnyWord,
				];

			case 'Select':
			case 'EnumReference':
				return [operators.In, operators.NotIn, operators.IsSet, operators.IsNotSet];
			case 'Expression':
			default:
				return [];
		}
	};

	const getAllOperators = () => Object.keys(operators).map((o) => operators[o]);

	const getOperatorByValue = (value) => operators[Object.keys(operators).find((o) => operators[o].value === value)];

	function getWords(value) {
		return value.split(/[, ;.]/).filter((f) => f);
	}

	return {
		operators: operators,
		getAllOperators: getAllOperators,
		getOperatorsByType: getOperatorsByType,
		getOperatorByValue: getOperatorByValue,
		getWords: getWords,
	};
}
